/**
 * Konami code -> spectacle FFX + conversion Al Bhed.
 * Sequence: up up down down left right left right b a
 *
 * Logique simplifiee :
 * - Le chiffrage se fait a CHAQUE affichage (pas persistent en fond)
 * - Chaque fois que la page devient visible, on re-lance la conversion 5s
 * - Pas de marquage de nodes, pas de MutationObserver
 */

const SEQUENCE = [
  'ArrowUp',
  'ArrowUp',
  'ArrowDown',
  'ArrowDown',
  'ArrowLeft',
  'ArrowRight',
  'ArrowLeft',
  'ArrowRight',
  'b',
  'a',
];

const AL_BHED_KEY = 'cof2-al-bhed';

const CIPHER: Record<string, string> = {
  a: 'y',
  b: 'p',
  c: 'l',
  d: 't',
  e: 'a',
  f: 'v',
  g: 'k',
  h: 'r',
  i: 'e',
  j: 'z',
  k: 'g',
  l: 'm',
  m: 's',
  n: 'h',
  o: 'b',
  p: 'x',
  q: 'n',
  r: 'q',
  s: 'd',
  t: 'j',
  u: 'f',
  v: 'c',
  w: 'w',
  x: 'i',
  y: 'u',
  z: 'o',
};

function toAlBhed(text: string): string {
  let result = '';
  for (const ch of text) {
    const lower = ch.toLowerCase();
    if (CIPHER[lower]) {
      const converted = CIPHER[lower];
      result += ch === ch.toUpperCase() ? converted.toUpperCase() : converted;
    } else {
      result += ch;
    }
  }
  return result;
}

function getTextNodes(root: Element): Text[] {
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, null);
  const nodes: Text[] = [];
  let node: Node | null = walker.nextNode();
  while (node) {
    if (node.textContent && node.textContent.trim().length > 0) {
      const parent = node.parentElement;
      if (parent && parent.tagName !== 'SCRIPT' && parent.tagName !== 'STYLE') {
        nodes.push(node as Text);
      }
    }
    node = walker.nextNode();
  }
  return nodes;
}

let navOriginals: { node: Text; original: string }[] | null = null;

function convertNav() {
  const nav = document.querySelector('.topbar');
  if (!nav) return;
  const needsRecapture =
    !navOriginals || navOriginals.length === 0 || navOriginals.some((e) => !e.node.isConnected);
  if (needsRecapture) {
    const nodes = getTextNodes(nav);
    navOriginals = nodes.map((n) => ({ node: n, original: n.textContent ?? '' }));
  }
  for (const entry of navOriginals ?? []) {
    entry.node.textContent = toAlBhed(entry.original);
  }
}

let activeConversionInterval: ReturnType<typeof setInterval> | null = null;

function runProgressiveConversion(durationMs: number) {
  if (activeConversionInterval) {
    clearInterval(activeConversionInterval);
    activeConversionInterval = null;
  }
  const main = document.querySelector('main.app-main');
  if (!main) return;
  convertNav();
  const nodes = getTextNodes(main);
  const targets = nodes.map((n) => ({ node: n, original: n.textContent ?? '' }));

  let totalChars = 0;
  for (const t of targets) totalChars += t.original.length;
  if (totalChars === 0) return;

  const converted: boolean[] = new Array(totalChars).fill(false);
  let convertedCount = 0;
  const startTime = Date.now();

  activeConversionInterval = setInterval(() => {
    const elapsed = Date.now() - startTime;
    const progress = Math.min(elapsed / durationMs, 1);
    const targetCount = Math.floor(progress * totalChars);

    while (convertedCount < targetCount) {
      const idx = Math.floor(Math.random() * totalChars);
      if (!converted[idx]) {
        converted[idx] = true;
        convertedCount++;
      }
    }

    let globalIdx = 0;
    for (const t of targets) {
      let result = '';
      for (let i = 0; i < t.original.length; i++) {
        if (converted[globalIdx]) {
          const ch = t.original[i] ?? '';
          const lower = ch.toLowerCase();
          const cipher = CIPHER[lower];
          if (cipher) {
            result += ch === ch.toUpperCase() ? cipher.toUpperCase() : cipher;
          } else {
            result += ch;
          }
        } else {
          result += t.original[i];
        }
        globalIdx++;
      }
      t.node.textContent = result;
    }

    if (progress >= 1) {
      if (activeConversionInterval) clearInterval(activeConversionInterval);
      activeConversionInterval = null;
    }
  }, 50);
}

function createPyreflies(container: HTMLElement) {
  for (let i = 0; i < 40; i++) {
    const fly = document.createElement('div');
    fly.className = 'pyrefly';
    fly.style.left = `${Math.random() * 100}%`;
    fly.style.top = `${Math.random() * 100}%`;
    fly.style.animationDelay = `${Math.random() * 2}s`;
    fly.style.animationDuration = `${2 + Math.random() * 3}s`;
    container.appendChild(fly);
  }
}

function createSphereGrid(container: HTMLElement) {
  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  svg.setAttribute('class', 'sphere-grid');
  svg.setAttribute('viewBox', '0 0 200 200');
  svg.innerHTML = `
    <circle cx="100" cy="100" r="80" fill="none" stroke="rgba(100, 200, 255, 0.3)" stroke-width="0.5" class="sphere-ring sphere-ring-1"/>
    <circle cx="100" cy="100" r="55" fill="none" stroke="rgba(100, 200, 255, 0.4)" stroke-width="0.5" class="sphere-ring sphere-ring-2"/>
    <circle cx="100" cy="100" r="30" fill="none" stroke="rgba(100, 200, 255, 0.5)" stroke-width="0.5" class="sphere-ring sphere-ring-3"/>
    <line x1="20" y1="100" x2="180" y2="100" stroke="rgba(100, 200, 255, 0.2)" stroke-width="0.3" class="sphere-line"/>
    <line x1="100" y1="20" x2="100" y2="180" stroke="rgba(100, 200, 255, 0.2)" stroke-width="0.3" class="sphere-line"/>
    <line x1="40" y1="40" x2="160" y2="160" stroke="rgba(100, 200, 255, 0.15)" stroke-width="0.3" class="sphere-line"/>
    <line x1="160" y1="40" x2="40" y2="160" stroke="rgba(100, 200, 255, 0.15)" stroke-width="0.3" class="sphere-line"/>
    <circle cx="100" cy="100" r="5" fill="rgba(100, 200, 255, 0.8)" class="sphere-core"/>
  `;
  container.appendChild(svg);
}

function speak(text: string) {
  if ('speechSynthesis' in window) {
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = 'ja-JP';
    utterance.rate = 0.8;
    utterance.pitch = 1.2;
    utterance.volume = 0.6;
    speechSynthesis.speak(utterance);
  }
}

function triggerSpectacle() {
  if (navigator.vibrate) navigator.vibrate([100, 50, 100, 50, 200]);

  const overlay = document.createElement('div');
  overlay.className = 'ffx-overlay';
  document.body.appendChild(overlay);

  createPyreflies(overlay);

  setTimeout(() => {
    createSphereGrid(overlay);
    overlay.classList.add('ffx-blue-tint');
  }, 800);

  setTimeout(() => {
    speak('Al Bhed');
  }, 1500);

  setTimeout(() => {
    localStorage.setItem(AL_BHED_KEY, 'true');
    runProgressiveConversion(5000);
  }, 2500);

  setTimeout(() => {
    overlay.classList.add('ffx-fade-out');
    setTimeout(() => overlay.remove(), 500);
  }, 3500);
}

export function isAlBhedActive(): boolean {
  return localStorage.getItem(AL_BHED_KEY) === 'true';
}

export function deactivateAlBhed(): void {
  localStorage.removeItem(AL_BHED_KEY);
  window.location.reload();
}

export function applyAlBhedIfActive(): void {
  if (!isAlBhedActive()) return;

  // Lancer apres que la page est rendue (delai genereux pour le rendu async)
  setTimeout(() => runProgressiveConversion(5000), 1200);

  // Re-lancer apres chaque navigation
  window.addEventListener('hashchange', () => {
    setTimeout(() => runProgressiveConversion(5000), 1200);
  });

  // Re-lancer quand on revient sur l'onglet
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) {
      setTimeout(() => runProgressiveConversion(5000), 500);
    }
  });
}

export function initKonami(): () => void {
  let position = 0;

  function onKey(e: KeyboardEvent) {
    if (e.key === SEQUENCE[position]) {
      position++;
      if (position === SEQUENCE.length) {
        position = 0;
        if (!isAlBhedActive()) {
          triggerSpectacle();
        }
      }
    } else {
      position = 0;
    }
  }

  document.addEventListener('keydown', onKey);
  return () => document.removeEventListener('keydown', onKey);
}
